import { inject, Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, first, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { PaginatableListOfAdmins } from 'src/app/swagger-api';
import { SessionService } from '../../auth/session.service';
import { OperationInfoService } from '../../common/long-operations/operation-info.service';
import { NotificationContextAttributesService } from '../../notifications/notifications-context-attributes.service';
import { DefaultService as RestApiService } from '../../swagger-api/api/default.service';

@Injectable({
  providedIn: 'root',
})
export class AdminService implements OnDestroy {
  private adminDeleted$: Subject<void> = new Subject<void>();
  private adminsListChanged$: ReplaySubject<PaginatableListOfAdmins> = new ReplaySubject<PaginatableListOfAdmins>();

  private subscriptions$: Subscription[] = [];

  private authService = inject(AuthService);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);
  private apiService = inject(RestApiService);
  private notificationContextService = inject(NotificationContextAttributesService);
  private translateService = inject(TranslateService);
  private operationInfoService = inject(OperationInfoService);

  public get adminDeleted(): Observable<void> {
    return this.adminDeleted$.asObservable();
  }

  public get adminsListChanged(): Observable<PaginatableListOfAdmins> {
    return this.adminsListChanged$.asObservable();
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public openSendSignupInvitationDialog(): void {
    this.notificationService
      .inviteAdminDialog(
        this.notificationContextService.getSendSignupInvitationDialogueArgs(
          this.translateService.instant('BTN_INVITE_NEW_ADMIN') as string,
          this.translateService.instant('FORM_EMAIL_INPUT_PLACEHOLDER') as string,
        ),
      )
      .pipe(first())
      .subscribe((email: string | null): void => {
        if (email) {
          this.sendSignupInvitation(email);
        }
      });
  }

  public sendSignupInvitation(email: string): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        successfulMessage: 'SEND_SIGNUP_INVITATION_SUCCESSFUL_OPERATION_DESCRIPTION',
      },
      () => {
        this.subscriptions$.push(
          this.apiService
            .restAdminSendSignupInvitationPost({ email: email, is_admin: true })
            .pipe(catchError(() => EMPTY))
            .subscribe(),
        );
      },
    );
  }

  public openRemoveAdminDialog(userId: string): void {
    this.notificationService
      .dialogDefault({
        title: 'REMOVE_ADMIN_DIALOG_TITLE',
        description: 'REMOVE_ADMIN_DIALOG_DESCRIPTION',
        buttons: this.notificationContextService.getRemoveAdminButtons(),
      })
      .pipe(first())
      .subscribe((response: string | boolean) => {
        if (response) {
          this.deleteUser(userId);
        }
      });
  }

  public deleteUser(userId: string): void {
    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        successfulMessage: 'REMOVE_ADMIN_SUCCESS_DESCRIPTION',
      },
      () => {
        this.subscriptions$.push(
          this.apiService
            .restUserDelete(userId)
            .pipe(catchError(() => EMPTY))
            .subscribe(() => {
              if (userId != this.sessionService.userId) {
                this.adminDeleted$.next();
              } else {
                this.authService.clientSideLogout();
              }
            }),
        );
      },
    );
  }

  public getAdminsList(sorting?: string, offset?: number, limit?: number): void {
    this.subscriptions$.push(
      this.apiService
        .restAdminAdminListGet(sorting, offset, limit)
        .pipe(
          first(),
          catchError(() => EMPTY),
        )
        .subscribe((data: PaginatableListOfAdmins) => {
          this.adminsListChanged$.next({ items: data.items, total_count: data.total_count });
        }),
    );
  }
}
